/* eslint-disable no-underscore-dangle */
/**
 * `/portal/operator` — the operator's assignments for Today / This week /
 * This month (Budapest), with the operator's own fee and the cash to
 * collect on each trip, who is driving it, whether the job has been
 * acknowledged, and how far along it is.
 *
 * Each row's Details (its own cell, at the end of the row) opens the trip
 * on its own screen (TripDetails.jsx,
 * handed the row through router state); Assign and Acknowledge are the
 * same controls there (TripActions.jsx).
 *
 * The customer's name is always there; the phone (a tel: link) only inside
 * the API's 24-hours-either-side-of-pickup window — before it, when it
 * will appear.
 *
 * Every figure on this screen is the OPERATOR's: `money` from
 * `/portal/operator/assignments` is its own fee and cash in its settlement
 * currency. The customer's price never reaches this screen — the API does
 * not send it.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import useUrlViewState from '../../../Hooks/useUrlViewState';
import {
  fetchOperatorAssignments,
  fetchOperatorStatement,
} from '../../../Services/OperatorPortalApi';
import { operatorAssignSource } from '../assignSources';
import { formatTime } from '../../../Utils/timezone';
import { telUrl } from '../../../Utils/contactLinks';
import Toast from '../Components/Toast';
import { ChevronRightIcon } from '../Components/Icons';
import AssignSheet from './AssignSheet';
import Pill from './Pill';
import RangeTabs from './RangeTabs';
import StatementSummary from './StatementSummary';
import {
  AcknowledgeButton, assignedNotice, DriverButton, useAcknowledge,
} from './TripActions';
import {
  ASSIGNMENT_RANGES,
  formatAmountView,
  formatMoney,
  monthRange,
  operatorLoadErrorMessage,
  phoneReleaseText,
  plural,
  rangeFor,
  rangeHeading,
  roleLabel,
  routeLabel,
  shortDay,
  statementBuckets,
  tripMeta,
  tripStatus,
} from './operatorFormat';

/**
 * The period being looked at, in the URL rather than in useState.
 *
 * React Router unmounts this screen the moment a row's Details is opened,
 * so a `useState` tab is gone by the time the operator comes back: a week
 * or a month of assignments silently reset to Today, which on a Monday
 * morning is an empty screen. The URL survives that, because going Back IS
 * the browser restoring a URL. See `Hooks/useUrlViewState`.
 *
 * `today` is the default, so an untouched screen keeps a clean address and
 * a link an operator shares carries only a period they actually chose.
 */
const ASSIGNMENTS_VIEW = { range: 'today' };

/** Only the periods this screen offers; `?range=` is hand-editable. */
const RANGE_KEYS = ASSIGNMENT_RANGES.map((option) => option.key);

function CustomerCell({ trip }) {
  const pending = phoneReleaseText(trip);
  if (!trip.customer_name && !trip.customer_phone && !pending) {
    return <span className="pp-op-muted">—</span>;
  }
  return (
    <>
      {trip.customer_name ? <span className="pp-op-customer">{trip.customer_name}</span> : null}
      {trip.customer_phone ? (
        <a className="pp-op-phone" href={telUrl(trip.customer_phone)}>{trip.customer_phone}</a>
      ) : null}
      {pending ? <span className="pp-op-small">{pending}</span> : null}
    </>
  );
}

function FeeCell({ trip }) {
  const { money } = trip;
  if (!money) return <span className="pp-op-muted">—</span>;
  const cancelled = trip.status === 'Cancelled';
  return (
    <>
      <span className="pp-op-fee">{formatMoney(money.fee, money.currency)}</span>
      {cancelled ? <span className="pp-op-small">Cancellation fee</span> : null}
      {money.adjustments ? (
        <span className="pp-op-small">{`incl. adjustment ${formatAmountView(money.adjustments)}`}</span>
      ) : null}
    </>
  );
}

function StatusCell({ trip, onAcknowledge, busy }) {
  const status = tripStatus(trip);
  return (
    <div className="pp-op-status">
      <Pill tone={status.tone}>{status.label}</Pill>
      <AcknowledgeButton trip={trip} busy={busy} onAcknowledge={onAcknowledge} />
    </div>
  );
}

export default function OperatorAssignments() {
  const { view, setView } = useUrlViewState(ASSIGNMENTS_VIEW);
  // A `?range=` nobody could have clicked shows the default, not an empty
  // tab strip over a list headed by some other period.
  const rangeKey = RANGE_KEYS.includes(view.range) ? view.range : ASSIGNMENTS_VIEW.range;
  const setRangeKey = useCallback((next) => setView({ range: next }), [setView]);
  const [data, setData] = useState({ trips: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [month, setMonth] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [notice, setNotice] = useState('');

  const range = useMemo(() => rangeFor(rangeKey), [rangeKey]);
  // Stable, so the toast's own timer isn't restarted by every render.
  const clearNotice = useCallback(() => setNotice(''), []);

  // Only the latest request may land. Switching Today → This week while
  // Today is still on the wire must not show Today's trips under the week.
  const latestLoad = useRef(0);

  const load = useCallback(async () => {
    latestLoad.current += 1;
    const ticket = latestLoad.current;
    setLoading(true);
    setError('');
    try {
      const result = await fetchOperatorAssignments(range);
      if (ticket === latestLoad.current) setData(result);
    } catch (failure) {
      if (ticket !== latestLoad.current) return;
      // Never leave the previous period's trips under this period's heading.
      setData({ trips: [] });
      setError(operatorLoadErrorMessage(failure));
    } finally {
      if (ticket === latestLoad.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
    // A new period, or leaving the screen: whatever is on the wire is stale.
    return () => { latestLoad.current += 1; };
  }, [load]);

  // Month-to-date money under the list. Not critical: if it fails, the
  // Payments tab still has the full statement.
  useEffect(() => {
    let cancelled = false;
    fetchOperatorStatement(monthRange(0))
      .then((statement) => { if (!cancelled) setMonth(statement); })
      .catch(() => { if (!cancelled) setMonth(null); });
    return () => { cancelled = true; };
  }, []);

  const monthBuckets = useMemo(() => statementBuckets(month), [month]);

  const { trips } = data;
  const withoutDriver = trips.filter((trip) => trip.needs_driver).length;
  // What is SHOWN names itself: the period the answer came back for (the
  // API echoes its own `from`/`to`), not the one that has just been tapped.
  // The rows of the last period stay on screen while the new one is read,
  // so a heading that had already moved on would be counting them under
  // days they did not run on. The dispatch board labels itself the same
  // way. A failure clears the rows, and with them the echo, so the period
  // that failed is what the heading names.
  const shownRange = data.from && data.to ? { from: data.from, to: data.to } : range;
  const multiDay = shownRange.from !== shownRange.to;
  const heading = rangeHeading(shownRange);
  // A read in flight with rows still under it — a period was tapped and
  // this is the last one, a moment out of date. Not the first paint, which
  // has nothing to quieten and gets the loading line instead.
  const quietRows = loading && trips.length > 0;

  const acknowledged = useCallback((trip, flag) => {
    setData((current) => ({
      ...current,
      trips: current.trips.map((t) => (t._id === trip._id ? { ...t, acknowledged: flag } : t)),
    }));
    setNotice(`${trip.booking_reference} acknowledged`);
  }, []);
  const { acknowledge, acknowledging } = useAcknowledge({
    onAcknowledged: acknowledged,
    onError: setError,
  });

  const assigned = async () => {
    const trip = assigning;
    setAssigning(null);
    setNotice(assignedNotice(trip));
    await load();
  };

  return (
    <div className="pp-op-page">
      <div className="pp-op-page__head">
        <div>
          <h1 className="pp-op-title">Assignments</h1>
          <div className="pp-op-subtitle">
            {`${heading} · ${plural(trips.length, 'trip', 'trips')}`}
          </div>
        </div>
        <div className="pp-op-page__tools">
          {withoutDriver ? (
            <div className="pp-op-alert" role="status">
              {`${plural(withoutDriver, 'trip', 'trips')} without a driver`}
            </div>
          ) : null}
          <RangeTabs
            options={ASSIGNMENT_RANGES}
            value={rangeKey}
            onChange={setRangeKey}
            busy={loading}
          />
        </div>
      </div>

      {error ? <div className="pp-op-error" role="alert">{error}</div> : null}

      {/* The list the period just tapped has not replaced yet stays here
          and goes quiet, rather than being swapped for a loading line on
          every tap: the bar under the tabs is what says the tap landed.
          The line is for a screen with nothing on it to keep — the first
          paint, or a period whose read failed and cleared the rows. */}
      <div
        className={`pp-op-card${quietRows ? ' pp-op-busy' : ''}`}
        aria-busy={quietRows || undefined}
      >
        {loading && !trips.length ? (
          <div className="pp-state" role="status">Loading your trips…</div>
        ) : null}

        {!loading && !trips.length && !error ? (
          <div className="pp-op-empty">No trips in this period.</div>
        ) : null}

        {trips.length ? (
          <table className="pp-op-table">
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Reference</th>
                <th scope="col">Route</th>
                <th scope="col">Customer</th>
                <th scope="col">Driver &amp; vehicle</th>
                <th scope="col" className="is-num">Your fee</th>
                <th scope="col" className="is-num">Cash</th>
                <th scope="col">Status</th>
                <th scope="col" className="is-details">
                  <span className="visually-hidden">Details</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {trips.map((trip) => (
                <tr
                  key={trip._id}
                  className={[
                    trip.status === 'Cancelled' ? 'is-cancelled' : '',
                    trip.needs_driver ? 'is-alert' : '',
                  ].filter(Boolean).join(' ') || undefined}
                >
                  <td data-label="Time">
                    <span className="pp-op-time">{formatTime(trip.date_datetime)}</span>
                    {multiDay ? <span className="pp-op-small">{shortDay(trip.date_datetime)}</span> : null}
                  </td>
                  <td data-label="Reference">
                    <span className="pp-op-ref">{trip.booking_reference}</span>
                  </td>
                  <td data-label="Route">
                    <span className="pp-op-route">{routeLabel(trip)}</span>
                    <span className="pp-op-small">
                      {[roleLabel(trip.role !== 'driver' ? trip.role : ''), tripMeta(trip)].filter(Boolean).join(' · ')}
                    </span>
                  </td>
                  <td data-label="Customer">
                    <CustomerCell trip={trip} />
                  </td>
                  <td data-label="Driver & vehicle">
                    <DriverButton trip={trip} onAssign={setAssigning} />
                  </td>
                  <td data-label="Your fee" className="is-num">
                    <FeeCell trip={trip} />
                  </td>
                  <td data-label="Cash" className="is-num">
                    <span className="pp-op-cash">{formatAmountView(trip.money?.cash_to_collect)}</span>
                  </td>
                  <td data-label="Status">
                    <StatusCell
                      trip={trip}
                      onAcknowledge={acknowledge}
                      busy={acknowledging === trip._id}
                    />
                  </td>
                  {/*
                    Its own cell at the end of the row: inside the reference
                    cell it wrapped into the reference itself
                    ("PU_DASH_E2E_COMPLETED" + "Details" read as one token).
                    `data-label` is empty so the phone card shows the control
                    alone, without a repeated "Details" label.
                  */}
                  <td data-label="" className="is-details">
                    <Link
                      className="pp-op-details-link"
                      to={`/portal/operator/trip/${trip._id}`}
                      state={{ trip }}
                      aria-label={`Details ${trip.booking_reference}`}
                    >
                      Details
                      <ChevronRightIcon size={14} width={2.5} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      {data.total > trips.length ? (
        <p className="pp-op-footnote">
          {`Showing the first ${trips.length} of ${data.total} trips. Choose a shorter period to see the rest.`}
        </p>
      ) : null}

      {/* Month to date. A login that answers for two settlement currencies
          gets one card per currency — each its own record's figures, side by
          side, never added together. One currency renders exactly one card,
          as it always has. */}
      {monthBuckets.map((bucket, index) => (
        <StatementSummary
          key={bucket.operator_id || `bucket-${index}`}
          statement={bucket}
          title={monthBuckets.length > 1
            ? `${monthRange(0).label} so far · ${bucket.currency}`
            : `${monthRange(0).label} so far`}
          subtitle={`${plural(Number(bucket.trip_count) || 0, 'trip', 'trips')} · settled in ${bucket.currency}`}
        />
      ))}

      {assigning ? (
        <AssignSheet
          trip={assigning}
          source={operatorAssignSource}
          onClose={() => setAssigning(null)}
          onAssigned={assigned}
        />
      ) : null}

      <Toast message={notice} onDone={clearNotice} />
    </div>
  );
}
